"use client";

import * as React from "react";
import { Box, Card, Flex, Text } from "@radix-ui/themes";
import { Skeleton, Button } from "./elements.js";
import { IconPanel } from "./icon-panel.js";
import { GenericError } from "./generic-error.js";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import * as CardList from "./card-list.js";
import { OrganizationDomain } from "../api/endpoint.js";
import { DomainItem } from "./domain-item.js";
import { getDomProps } from "./utils.js";
import type { WidgetRootDomProps, WidgetRootState } from "./utils.js";
import { Translation } from "./i18n/translation.js";

interface AdminPortalDomainVerificationProps extends WidgetRootDomProps {
  organizationDomains: OrganizationDomain[];
  onAddDomain: () => void;
  onDeleteDomain: (domainId: string) => void;
  onReverifyDomain: (domainId: string) => void;
  isPending?: boolean;
}

const AdminPortalDomainVerification = ({
  organizationDomains,
  onAddDomain,
  onDeleteDomain,
  onReverifyDomain,
  isPending = false,
  ...domProps
}: AdminPortalDomainVerificationProps) => {
  if (organizationDomains.length === 0) {
    return (
      <Card size="2" {...getWidgetRootDomProps("resolved", domProps)}>
        <Flex direction="row" justify="between" align="center">
          <Text size="2" color="gray">
            <Translation
              defaultMessage="You haven't added any verified domains yet."
              id="t/D0Sf"
              description="Empty state message when no domains have been added"
            />
          </Text>
          <Button
            variant="secondary"
            loading={isPending}
            disabled={isPending}
            onClick={onAddDomain}
          >
            <Translation
              defaultMessage="Add domain"
              id="APksY2"
              description="Button text to add a new domain"
            />
            <ExternalLinkIcon aria-hidden />
          </Button>
        </Flex>
      </Card>
    );
  }

  return (
    <Flex
      direction="column"
      gap="4"
      {...getWidgetRootDomProps("resolved", domProps)}
    >
      <CardList.Root>
        {organizationDomains.map((domain) => (
          <CardList.Item key={domain.id}>
            <DomainItem
              domain={domain}
              onDeleteDomain={onDeleteDomain}
              onReverifyDomain={onReverifyDomain}
            />
          </CardList.Item>
        ))}
      </CardList.Root>

      <Box>
        <Button
          variant="secondary"
          loading={isPending}
          disabled={isPending}
          onClick={onAddDomain}
        >
          <Translation
            defaultMessage="Add domain"
            id="APksY2"
            description="Button text to add a new domain"
          />
          <ExternalLinkIcon aria-hidden />
        </Button>
      </Box>
    </Flex>
  );
};

interface AdminPortalDomainVerificationLoadingProps extends WidgetRootDomProps {}

const AdminPortalDomainVerificationLoading: React.FC<
  AdminPortalDomainVerificationLoadingProps
> = (domProps) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("loading", domProps)}>
      <Flex gap="4" align="center">
        <Skeleton>
          <IconPanel />
        </Skeleton>

        <Flex direction="column">
          <Text size="2" highContrast weight="bold" as="p" mb="-2px">
            <Skeleton>
              <Translation
                defaultMessage="Domain name"
                id="O2vxRJ"
                description="Placeholder text for domain name loading state"
              />
            </Skeleton>
          </Text>

          <Text size="2" color="gray" as="p">
            <Skeleton>
              <Translation
                defaultMessage="Added recently"
                id="w3A6MN"
                description="Placeholder text for domain added date loading state"
              />
            </Skeleton>
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
};

interface AdminPortalDomainVerificationErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const AdminPortalDomainVerificationError: React.FC<
  AdminPortalDomainVerificationErrorProps
> = ({ error, ...domProps }) => {
  return (
    <Card size="2" {...getWidgetRootDomProps("error", domProps)}>
      <GenericError error={error} />
    </Card>
  );
};

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "admin-portal-domain-verification",
    widgetState: state,
  });
}

export type {
  AdminPortalDomainVerificationProps,
  AdminPortalDomainVerificationLoadingProps,
  AdminPortalDomainVerificationErrorProps,
};
export {
  AdminPortalDomainVerification,
  AdminPortalDomainVerificationLoading,
  AdminPortalDomainVerificationError,
};
